/**
 * The application frame (the prototype's `render()`): `.workspace` with the top bar, the banners,
 * `.workspace-content` (sidebar + the open screen) and the phone folder bar. Classes are the prototype's:
 * `is-settings`, `with-details`, `sidebar-collapsed` and `mobile-<screen>` — on phones the route decides
 * the screen (a message route is the reader), overlays add navigation and Details (web.md §5).
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReaderScreen } from '../reader/index.js';
import { findCategory } from '../settings/registry.js';
import { SettingsLayout } from '../settings/shell/SettingsLayout.js';
import { Icon } from '../icons/Icon.js';
import { Tool } from '../ui/Tool.js';
import { useEngine } from '../engine-client/EngineProvider.js';
import { Banners } from './Banners.js';
import { useFolders, useLocalSettings } from './hooks.js';
import { rememberReaderPath } from './navigation.js';
import { parseReaderPath } from './reader-route.js';
import { Sidebar, type SidebarSelection } from './Sidebar.js';
import { Topbar } from './Topbar.js';
import { TopbarSlotContext } from './topbar-slot.js';
import { ui, useUi } from './ui-store.js';

export type MobileScreen = 'navigation' | 'list' | 'reader' | 'details' | 'settings-list' | 'settings-editor';

export function Workspace({ mode }: { mode: 'reader' | 'settings' }) {
  const location = useLocation();
  const params = useParams();
  const engine = useEngine();
  const local = useLocalSettings();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const detailsOpen = useUi((s) => s.detailsOpen);
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const overlay = useUi((s) => s.mobileOverlay);
  const reader = mode === 'reader' ? parseReaderPath(location.pathname) : null;

  // The phone screen follows the route; leaving a screen closes its overlays.
  useEffect(() => {
    ui.set({ mobileOverlay: 'none' });
  }, [location.pathname]);

  // Remember where the reader was (Back to reader) and the last view (the start screen).
  useEffect(() => {
    if (mode !== 'reader' || !reader) return;
    rememberReaderPath(location.pathname);
    const lastView = reader.scope.kind === 'folder' ? { kind: 'folder' as const, id: reader.scope.folderId } : reader.scope.kind === 'view' ? { kind: 'view' as const, id: reader.scope.viewId } : null;
    if (!lastView || !local) return;
    const prev = local.ui.lastView;
    if (prev.kind === lastView.kind && prev.id === lastView.id) return;
    void engine.settings.setLocal('ui', { ...local.ui, lastView }).catch(() => undefined);
    // Only the scope matters, not the open message.
  }, [mode, location.pathname, local?.ui.lastView.id, local?.ui.lastView.kind]);

  const selection: SidebarSelection =
    mode === 'settings'
      ? { kind: 'settings', category: params.category ?? 'sources' }
      : reader?.scope.kind === 'folder'
        ? { kind: 'folder', id: reader.scope.folderId }
        : reader?.scope.kind === 'view'
          ? { kind: 'view', id: reader.scope.viewId }
          : { kind: 'corrections' };

  const screen: MobileScreen =
    overlay === 'navigation'
      ? 'navigation'
      : mode === 'settings'
        ? params.recordId
          ? 'settings-editor'
          : 'settings-list'
        : overlay === 'details'
          ? 'details'
          : reader?.messageId
            ? 'reader'
            : 'list';

  const classes = ['workspace', mode === 'settings' ? 'is-settings' : '', detailsOpen ? 'with-details' : '', collapsed ? 'sidebar-collapsed' : '', `mobile-${screen}`].filter(Boolean).join(' ');
  const category = mode === 'settings' ? findCategory(params.category) : undefined;

  return (
    <TopbarSlotContext.Provider value={slot}>
      <div className={classes}>
        <Topbar mode={mode} slotRef={setSlot} {...(category ? { categoryName: category.name } : {})} {...(category?.filterDialog ? { filterDialog: category.filterDialog } : {})} />
        <Banners />
        <div className="workspace-content">
          <Sidebar selection={selection} />
          {mode === 'settings' ? <SettingsLayout /> : <ReaderScreen />}
        </div>
        <MobileFolderBar active={selection.kind === 'folder' ? selection.id : null} />
      </div>
    </TopbarSlotContext.Provider>
  );
}

/** The phone's bottom bar: the folders, then Settings (the prototype's `.mobile-folder-bar`). */
function MobileFolderBar({ active }: { active: string | null }) {
  const folders = useFolders();
  const navigate = useNavigate();
  return (
    <div className="mobile-folder-bar">
      {folders.map((f) => (
        <button type="button" key={f.id} aria-label={f.name} className={active === f.id ? 'active' : undefined} onClick={() => navigate(`/f/${encodeURIComponent(f.id)}`)}>
          <Icon name={f.icon} />
          <span>{f.name}</span>
        </button>
      ))}
      <Tool icon="settings" label="Settings" onClick={() => navigate('/settings/sources')} />
    </div>
  );
}
